import { Typography } from "@deere/fuel-react";
import { Option, Select } from "@deere/fuel-react";
import React, { forwardRef, useState, useImperativeHandle } from "react";
import { getSubDealerDetails, PostAddressControlTowerChanges } from "../api";
import { log } from "../../Helper";
import { toast } from "react-toastify";

const AddressContent = React.memo(
  forwardRef((props, ref) => {
    const {
      isCloseClicked,
      selectedMilestoneDetails,
      setIsLoading,
      isPathEditable,
      isLastPath,
      isEditClicked,
      tabValue,
      getShipToDetailsForMachine,
    } = props;

    const list = tabValue?.startAccountList;

    const [selected, setSelected] = useState(null);
    const [isSelectedInAddress, setIsSelectedInAddress] = useState(false);
    const [subDealerList, setSubDealerList] = useState(null);
    const [selectedSub, setSelectedSub] = useState(null);
    const [isSubSelectedInAddress, setIsSubSelectedInAddress] = useState(false);
    const [newSubDealerData, setNewSubDealerData] = useState(null);
    const [newMainDealerData, setNewMainDealerData] = useState(null);

    const Account =
      tabValue?.type === "DEALER"
        ? tabValue.DealerAccount
        : tabValue.HubAccount;
    const Outlet =
      tabValue?.type === "DEALER" ? tabValue.DealerOutlet : tabValue.HubOutlet;

    const handleSelection = (data) => {
      // Clear form when selecting main dealer
      clearForm();
      if (tabValue.Account === data) {
        setIsSelectedInAddress(false);
      } else {
        // Setting the sub dealer as null when main dealer is selected to clear the state
        setIsSubSelectedInAddress(false);
        setSelectedSub(null);
        setNewSubDealerData(null);
        setIsSelectedInAddress(true);
        setSelected(data);
        setNewMainDealerData(data);
      }
    };

    const getSubDealerList = async (selected) => {
      if (tabValue.Account !== selected) {
        log("getSubDealerList selected", selected);
        const selectedDealerAccount = selected.substring(
          selected.indexOf("(") + 1,
          selected.length - 1
        );
        log("selectedDealerAccount", selectedDealerAccount);
        const response = await getSubDealerDetails(selectedDealerAccount);
        if (response) {
          setSubDealerList(response); // Set the list of sub dealers based on the selected main dealer
        }
        log("subDealerList", response);
      }
    };

    const handleSubSelection = (data) => {
      clearForm(); // Clear form when selecting sub dealer
      log("handleSubSelection data", data);
      setIsSubSelectedInAddress(true);
      setSelectedSub(data);
      setNewSubDealerData(data);
    };

    const [customAddressFormData, setCustomAddressFormData] = useState({
      accountId: "",
      accountName: "",
      physicalStreetAddress1: "",
      physicalPostalCode: "",
      physicalCity: "",
      physicalCountry: "",
    });

    const clearForm = () => {
      setCustomAddressFormData({
        accountId: "",
        accountName: "",
        physicalStreetAddress1: "",
        physicalPostalCode: "",
        physicalCity: "",
        physicalCountry: "",
      });
    };

    const handleCustomAddressSelection = (data, field) => {
      log("handleCustomSubSelection data", data);

      setCustomAddressFormData((prevState) => {
        const newState = {
          ...prevState,
          [field]: data,
        };

        setNewSubDealerData(newState);

        return newState; // Return the new state to update the state
      });

      setIsSubSelectedInAddress(true);
      // setSelectedSub(data); // Uncomment if needed
    };

    // Function to validate required fields
    const validateFields = (data) => {
      log("data in validateFields", data);
      const requiredFields = [
        { key: "shipToAccountName", label: "Account Name" },
        { key: "shipToAccountStreet", label: "Street Address" },
        { key: "shipToAccountZip", label: "Postal Code" },
        { key: "shipToAccountCity", label: "City" },
        { key: "shipToAccountCountry", label: "Country" },
      ];

      // Check each required field for validity
      for (const field of requiredFields) {
        if (!data[field.key]) {
          toast.error(`${field.label} cannot be null or empty.`);
          return false; // Return false if any field is invalid
        }
      }
      return true; // All validations passed
    };

    const handleSave = async () => {
      console.log("Address => handleSave called");
      if (newSubDealerData) {
        if (newSubDealerData.accountId !== tabValue.DealerAccount) {
          const orderPathId = tabValue.orderPathId.toString();
          const comar = [tabValue.orderUnit, tabValue.orderNumber].join("");
          log("comar value in save", comar);
          log("Main Dealer Data in save", newMainDealerData);
          const requestDTO = {
            orderUnit: tabValue.orderUnit,
            orderNumber: tabValue.orderNumber,
            orderYear: tabValue.orderYear,
            orderPathId: orderPathId,
            mainDealer: newMainDealerData.substring(
              newMainDealerData.indexOf("(") + 1,
              newMainDealerData.length - 1
            ),
            shipToAccount: newSubDealerData?.accountId
              ? newSubDealerData?.accountId
              : "000000", // Default value for other
            shipToAccountName: newSubDealerData?.accountName,
            shipToAccountStreet: newSubDealerData?.physicalStreetAddress1,
            shipToAccountZip: newSubDealerData?.physicalPostalCode,
            shipToAccountCity: newSubDealerData?.physicalCity,
            shipToAccountCountry: newSubDealerData?.physicalCountry,
            userId: tabValue.userId,
            isShipHold:
              selectedMilestoneDetails?.pathDetails?.isShipHold ?? false,
            reason: selectedMilestoneDetails?.pathDetails?.reason ?? null,
            estimatedReleaseDate:
              selectedMilestoneDetails?.pathDetails?.estimatedReleaseDate ??
              null,
            comments: selectedMilestoneDetails?.pathDetails?.comments ?? null,
            isUrgent: selectedMilestoneDetails?.pathDetails?.isUrgent ?? false,
          };
          log("requestDTO in save", requestDTO);

          // Validate fields before making API call
          if (validateFields(requestDTO)) {
            setIsLoading(true);
            const response = await PostAddressControlTowerChanges(requestDTO);
            log("response of PostAddressControlTowerChanges", response);
            if (response.status === 200) {
              // setIsSaveSuccessful(true);
              toast.success(response.data.message);
            } else {
              // setIsSaveSuccessful(false);
              toast.error(response.data.message);
            }
            setIsLoading(false);
            getShipToDetailsForMachine(comar); // Refresh ship to details
          }
        }
      } else {
        toast.error("Please fill in the required fields.");
      }
    };

    const onCloseResetValuesIfNotSaved = () => {
      console.log("Address => onCloseResetValuesIfNotSaved called");

      setSelected(null);
      setIsSelectedInAddress(false);
      setSubDealerList(null);
      setSelectedSub(null);
      setIsSubSelectedInAddress(false);
      setNewSubDealerData(null);
      setNewMainDealerData(null);

      clearForm();
    };

    useImperativeHandle(
      ref,
      () => ({
        handleSave,
        onCloseResetValuesIfNotSaved,
      }),
      [handleSave]
    );

    return (
      <div className="flex flex-col gap-3 h-[400px]">
        {/* Displaying the start, destination, and forwarder information */}
        <div className="flex">
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Start
            </Typography>
            <Typography fontWeight="400" fontSize={16}>
              {tabValue.start}
            </Typography>
          </div>
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Destination
            </Typography>
            <Typography fontWeight="400" fontSize={16}>
              {tabValue.destination}
            </Typography>
          </div>
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Forwarder
            </Typography>
            <Typography fontWeight="400" fontSize={16}>
              {tabValue.forwarder}
            </Typography>
          </div>
        </div>

        {/* Account selection section */}
        <div className="flex">
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Account
            </Typography>
            {isLastPath &&
            isEditClicked &&
            isPathEditable &&
            !isCloseClicked ? (
              // Dropdown for selecting the main dealer account
              <Select className="static-select" placeholder={tabValue.Account}>
                {list?.map((startSelect) => (
                  <Option
                    onClick={() => {
                      handleSelection(startSelect);
                      getSubDealerList(startSelect);
                    }}
                    value={startSelect}
                  >
                    {startSelect}
                  </Option>
                ))}
              </Select>
            ) : isLastPath && isSubSelectedInAddress && !isCloseClicked ? (
              // Display the selected account if a sub address is selected
              <Typography fontWeight="400" fontSize={16}>
                {selected}{" "}
              </Typography>
            ) : (
              // Default account display
              <Typography fontWeight="400" fontSize={16}>
                {Account}
              </Typography>
            )}
          </div>

          {/* Sub address selection section */}
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              SubAddress/Outlet
            </Typography>
            {isLastPath &&
            isSelectedInAddress &&
            isEditClicked &&
            !isCloseClicked ? (
              // Dropdown for selecting the sub address
              <Select className="static-select" placeholder={tabValue.Account}>
                <Option
                  onClick={() => {
                    handleSubSelection("OTHER");
                  }}
                >
                  Other
                </Option>
                {subDealerList?.map((startSubSelect, index) => (
                  <Option
                    key={index}
                    onClick={() => {
                      handleSubSelection(startSubSelect);
                    }}
                    value={startSubSelect}
                  >
                    {startSubSelect.accountId} {startSubSelect.physicalCity}
                  </Option>
                ))}
              </Select>
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address if one is chosen
              <>
                <Typography fontWeight="400" fontSize={16}>
                  {selectedSub.accountId} {selectedSub.physicalCity}
                </Typography>
              </>
            ) : (
              // Default outlet display
              <Typography fontWeight="400" fontSize={16}>
                {Outlet}{" "}
              </Typography>
            )}
          </div>

          {/* Name input section */}
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Name*
            </Typography>
            {selectedSub === "OTHER" &&
            isLastPath &&
            isEditClicked &&
            !isCloseClicked ? (
              // Input field for custom name if "Other" is selected
              <input
                type="text"
                onChange={(e) => {
                  handleCustomAddressSelection(e.target.value, "accountName");
                }}
                className="border h-9 border-gray-300 rounded-md p-2"
              />
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address name
              <Typography fontWeight="400" fontSize={16}>
                {selectedSub.accountName}
              </Typography>
            ) : (
              // Default name display
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.toName}
              </Typography>
            )}
          </div>
        </div>

        {/* Street address input section */}
        <div className="flex">
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Street*
            </Typography>
            {selectedSub === "OTHER" &&
            isLastPath &&
            isEditClicked &&
            !isCloseClicked ? (
              // Input field for custom street address if "Other" is selected
              <input
                type="text"
                onChange={(e) => {
                  handleCustomAddressSelection(
                    e.target.value,
                    "physicalStreetAddress1"
                  );
                }}
                className="border h-9 border-gray-300 rounded-md p-2 focus:border-green-500 transition duration-150"
              />
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address street
              <Typography fontWeight="400" fontSize={16}>
                {selectedSub.physicalStreetAddress1}
              </Typography>
            ) : (
              // Default street address display
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.toStreet}
              </Typography>
            )}
          </div>

          {/* Zip code input section */}
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Zip*
            </Typography>
            {selectedSub === "OTHER" &&
            isLastPath &&
            isEditClicked &&
            !isCloseClicked ? (
              // Input field for custom zip code if "Other" is selected
              <input
                type="text"
                onChange={(e) => {
                  handleCustomAddressSelection(
                    e.target.value,
                    "physicalPostalCode"
                  );
                }}
                className="border h-9 border-gray-300 rounded-md p-2"
              />
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address zip code
              <Typography fontWeight="400" fontSize={16}>
                {selectedSub.physicalPostalCode}
              </Typography>
            ) : (
              // Default zip code display
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.toZip}
              </Typography>
            )}
          </div>

          {/* City input section */}
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              City*
            </Typography>
            {selectedSub === "OTHER" &&
            isLastPath &&
            isEditClicked &&
            !isCloseClicked ? (
              // Input field for custom city name if "Other" is selected
              <input
                type="text"
                onChange={(e) => {
                  handleCustomAddressSelection(e.target.value, "physicalCity");
                }}
                className="border h-9 border-gray-300 rounded-md p-2"
              />
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address city
              <Typography fontWeight="400" fontSize={16}>
                {selectedSub.physicalCity}
              </Typography>
            ) : (
              // Default city display
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.toCity}
              </Typography>
            )}
          </div>
        </div>

        {/* Country input section */}
        <div className="flex">
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Country*
            </Typography>
            {selectedSub === "OTHER" &&
            isLastPath &&
            isEditClicked &&
            !isCloseClicked ? (
              // Input field for custom country if "Other" is selected
              <input
                type="text"
                onInput={(e) => {
                  handleCustomAddressSelection(
                    e.target.value,
                    "physicalCountry"
                  );
                }}
                className="border h-9 border-gray-400 rounded-md p-2"
              />
            ) : isLastPath &&
              isSubSelectedInAddress &&
              isEditClicked &&
              !isCloseClicked ? (
              // Display the selected sub address country
              <Typography fontWeight="400" fontSize={16}>
                {selectedSub.physicalCountry}
              </Typography>
            ) : (
              // Default country display
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.toCountry}
              </Typography>
            )}
          </div>
        </div>

        {/* Shipping instructions section */}
        <div className="flex">
          <div className="flex flex-col gap-1 w-[300px]">
            <Typography fontWeight={"700"} fontSize={14}>
              Shipping Free Text
            </Typography>
            <div
              className={`w-[800px] max-h-[120px] overflow-y-auto break-words rounded-md ${
                tabValue.shippingInstructions
                  ? "border border-gray-200 p-2 bg-gray-50"
                  : "border-0 p-0 bg-transparent"
              }`}
            >
              <Typography fontWeight="400" fontSize={16}>
                {tabValue.shippingInstructions}
              </Typography>
            </div>
          </div>
        </div>
      </div>
    );
  })
);
export default AddressContent;
